import React, { createContext, useContext, useState, useEffect } from 'react';
import { useMantineColorScheme } from '@mantine/core';
import { useAppSelector } from '../store/hooks'; // ✅ add this

interface ThemeContextType {
  isDarkMode: boolean;
  toggleTheme: () => void;
  language: string;
  setLanguage: (lang: string) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState('en');
  const { colorScheme, setColorScheme } = useMantineColorScheme();

  const theme = useAppSelector((state) => state.theme); // ✅ get backend theme

  const isDarkMode = colorScheme === 'dark';

  const toggleTheme = () => {
    setColorScheme(isDarkMode ? 'light' : 'dark');
  };

  // ✅ Load language
  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      setLanguage(savedLanguage);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  // ✅ APPLY BACKEND THEME (IMPORTANT)
  useEffect(() => {
    if (!theme) return;

    const root = document.documentElement;

    root.style.setProperty('--primary-color', theme.primaryColor);
    root.style.setProperty('--secondary-color', theme.secondaryColor);
    root.style.setProperty('--success-color', theme.successColor);
    root.style.setProperty('--warning-color', theme.warningColor);
    root.style.setProperty('--error-color', theme.errorColor);
    root.style.setProperty('--info-color', theme.infoColor);
    root.style.setProperty('--font-family', theme.fontFamily);
  }, [theme]);

  return (
    <ThemeContext.Provider
      value={{ isDarkMode, toggleTheme, language, setLanguage }}
    >
      {children}
    </ThemeContext.Provider>
  );
};